import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, shareReplay, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class IconCacheService {
  /**
   * ================================================
   * Dependencies
   * ================================================
   */
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);
  /**
   * ================================================
   * Private properties
   * ================================================
   */
  private readonly iconCache = new Map<string, Observable<SafeHtml>>();
  /**
   * Gets an icon from cache or loads it from the network if not cached.
   * Uses shareReplay to ensure the same HTTP request is not made multiple times.
   * @param iconPath The path to the SVG icon
   * @returns Observable<SafeHtml> The sanitized SVG content
   */
  getIcon(iconPath: string): Observable<SafeHtml> {
    // Check if icon is already cached
    if (this.iconCache.has(iconPath)) {
      const cachedIcon = this.iconCache.get(iconPath);
      if (cachedIcon) {
        return cachedIcon;
      }
    }
    // Create new observable for the icon
    const icon$ = this.http.get(iconPath, { responseType: 'text' }).pipe(
      map((svgContent: string) => this.sanitizer.bypassSecurityTrustHtml(svgContent)),
      shareReplay(1) // Cache the result so future subscribers get the cached value
    );

    // Store in cache before returning
    this.iconCache.set(iconPath, icon$);

    return icon$;
  }
  /**
   * Preloads multiple icons to cache them before they're needed.
   * Useful for preloading commonly used icons.
   * @param iconPaths Array of icon paths to preload
   */
  preloadIcons(iconPaths: string[]): void {
    iconPaths.forEach(path => {
      if (!this.iconCache.has(path)) {
        this.getIcon(path).subscribe(); // Trigger the HTTP request to cache the icon
      }
    });
  }
  /**
   * Clears the icon cache. Useful for testing or when you want to force fresh requests.
   */
  clearCache(): void {
    this.iconCache.clear();
  }
  /**
   * Gets the current cache size (number of cached icons).
   */
  getCacheSize(): number {
    return this.iconCache.size;
  }
  /**
   * Checks if an icon is already cached.
   * @param iconPath The path to check
   */
  isCached(iconPath: string): boolean {
    return this.iconCache.has(iconPath);
  }
}
